import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createOrder } from '../store/ordersSlice.js';
import { fetchAddresses, selectAddress } from '../store/addressSlice.js';
import AddressManager from './AddressManager.js';
import { loadRazorpay, createRazorpayOrder } from '../utils/razorpay.js';
import API_URL from '../config/apiConfig.js';

const Checkout = () => {
  const user = useSelector((s) => s.auth.user);
  const items = useSelector((s) => s.cart.items || []);
  const addresses = useSelector((s) => s.addresses.items); // eslint-disable-line no-unused-vars
  const selectedAddress = useSelector((s) => s.addresses.selectedAddress);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [paying, setPaying] = useState(false);

  // ── Phone OTP state ──
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchAddresses(user.uid));
    }
  }, [dispatch, user]);

  // Pre-fill phone from selected address
  useEffect(() => {
    if (selectedAddress?.phone && !otpVerified) {
      setPhone(selectedAddress.phone);
    }
  }, [selectedAddress, otpVerified]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown <= 0) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [countdown]);

  if (!user) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="card border-0 shadow-sm rounded-4 p-5">
              <i className="bi bi-lock display-4 text-muted mb-3"></i>
              <h3 className="fw-bold mb-3">Sign In Required</h3>
              <p className="text-muted mb-4">
                Please sign in to your account to proceed with checkout
              </p>
              <button
                className="btn btn-dark rounded-pill px-4 py-3"
                style={{ background: "#2f0f0c" }}
                onClick={() => navigate("/signin")}
              >
                <i className="bi bi-person-circle me-2"></i>
                Sign In to Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = items.reduce(
    (sum, item) => sum + (item.price || 0) * (item.qty || 1),
    0,
  );
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  const handleAddressSelect = (address) => {
    setSelectedAddressId(address.id);
    dispatch(selectAddress(address));
  };

  const handleContinueToReview = () => {
    if (!selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }
    setStep(2);
    toast.success("Address confirmed");
  };

  const handlePayment = () => {
    setStep(3);
  };

  // ── Send phone OTP ──
  const handleSendOtp = async () => {
    const cleaned = phone.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/send-phone-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: cleaned }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
        setCountdown(300);
        toast.success(data.message);
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch {
      toast.error("Could not reach server. Check your connection.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Verify phone OTP ──
  const handleVerifyOtp = async () => {
    if (otp.length < 4) { toast.error("Enter the OTP"); return; }
    setOtpLoading(true);
    try {
      const cleaned = phone.replace(/\D/g, "").slice(-10);
      const res = await fetch(`${API_URL}/api/verify-phone-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: cleaned, otp }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success("Phone verified! Proceeding to payment.");
      } else {
        toast.error(data.message || "Invalid OTP");
      }
    } catch {
      toast.error("Verification failed. Try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleRazorpayPayment = async () => {
    if (paying) return;
    if (!selectedAddress) { toast.error("Please select a delivery address"); return; }
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (total <= 0) { toast.error("Invalid order amount"); return; }

    setPaying(true);
    try {
      const res = await loadRazorpay();
      if (!res) {
        toast.error("Payment gateway failed to load. Check your internet connection.");
        setPaying(false);
        return;
      }

      const { options } = await createRazorpayOrder(total);

      options.prefill = {
        name: selectedAddress?.fullName || user.name || "",
        email: user.email || "",
        contact: selectedAddress?.phone || "",
      };

      // ✅ ORDER IS ONLY CREATED HERE — inside handler — which Razorpay calls ONLY on successful payment
      options.handler = async (response) => {
        const paymentId = response.razorpay_payment_id;       // e.g. pay_xxxxx
        const razorpayOrderId = response.razorpay_order_id || ""; // e.g. order_xxxxx
        const signature = response.razorpay_signature || "";

        // Double-check: payment ID must start with "pay_" — Razorpay's proof of payment
        if (!paymentId || !paymentId.startsWith("pay_")) {
          toast.error("Payment could not be verified. No order placed. Contact support.");
          setPaying(false);
          return;
        }

        // Use Razorpay's order_id as our order key in DB
        const orderId = razorpayOrderId || `ORD-${paymentId}`;

        const orderData = {
          userId: user.uid,
          email: user.email || "",
          name: selectedAddress?.fullName || "",
          phone: selectedAddress?.phone || phone || "",
          houseNo: selectedAddress?.houseNo || "",
          street: selectedAddress?.street || "",
          area: selectedAddress?.area || "",
          landmark: selectedAddress?.landmark || "",
          city: selectedAddress?.city || "",
          state: selectedAddress?.state || "",
          pincode: selectedAddress?.pincode || "",
          items: items.map((i) => ({
            id: i.id || "",
            name: i.name || "",
            qty: i.qty || 1,
            price: i.price || 0,
          })),
          subtotal,
          gst,
          total,
          status: "confirmed",
          paymentStatus: "paid",
          paymentId,           // pay_xxxxx — Razorpay proof of payment
          razorpayOrderId,     // order_xxxxx — Razorpay order ID
          razorpaySignature: signature,
          orderId,
          createdAt: Date.now(),
        };

        try {
          const result = await dispatch(createOrder(orderData)).unwrap();
          navigate("/order-success", {
            state: { orderData: result, paymentVerified: true, orderId },
          });
        } catch (err) {
          // Payment succeeded even if DB write failed — still show success
          navigate("/order-success", {
            state: { orderData, paymentVerified: true, orderId },
          });
        }
      };

      // ❌ Called when user closes modal WITHOUT paying — no order placed
      options.modal = {
        ondismiss: () => {
          toast.warning("Payment cancelled. No order was placed.");
          setPaying(false);
        },
        escape: true,
        backdropclose: false,
      };

      const rzp = new window.Razorpay(options);

      // ❌ Called on payment failure — no order placed
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description}. No order placed.`);
        setPaying(false);
      });

      rzp.open();
    } catch (error) {
      toast.error("Payment setup failed. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="container py-5">
      <style>{`
        .step-indicator {
          transition: all 0.3s ease;
          position: relative;
        }
        .step-indicator.active {
          background: linear-gradient(135deg, #2f0f0c, #4a1a15) !important;
          transform: scale(1.1);
          box-shadow: 0 4px 15px rgba(47, 15, 12, 0.3);
        }
        .step-line {
          position: absolute;
          top: 50%;
          left: 60px;
          right: -60px;
          height: 2px;
          background: #e9ecef;
          z-index: -1;
        }
        .step-line.completed {
          background: #2f0f0c;
        }
        .checkout-card {
          transition: all 0.3s ease;
        }
        .checkout-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        .animate-slide-in {
          animation: slideInUp 0.5s ease-out;
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* PROGRESS STEPS */}
      <div className="row mb-5">
        <div className="col-12">
          <div className="d-flex justify-content-center align-items-center mb-4 position-relative">
            {[
              { num: 1, label: "Address", icon: "geo-alt" },
              { num: 2, label: "Review", icon: "clipboard-check" },
              { num: 3, label: "Verify", icon: "phone" },
              { num: 4, label: "Payment", icon: "credit-card" },
            ].map((stepItem, index) => (
              <div
                key={stepItem.num}
                className="d-flex align-items-center position-relative"
              >
                <div
                  className={`step-indicator d-flex align-items-center justify-content-center rounded-circle fw-bold ${
                    step >= stepItem.num
                      ? "active text-white"
                      : "bg-light text-muted"
                  }`}
                  style={{ width: 48, height: 48, zIndex: 1, fontSize: 14 }}
                >
                  <i
                    className={`bi bi-${stepItem.icon}`}
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                {index < 3 && (
                  <div
                    className={`step-line ${step > stepItem.num ? "completed" : ""}`}
                    style={{
                      width: 80,
                      marginLeft: 6,
                      marginRight: 6,
                    }}
                  ></div>
                )}
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-center">
            <div
              className="d-flex checkout-step-labels"
              style={{ width: "360px", justifyContent: "space-between" }}
            >
              <span className={`small fw-semibold ${step >= 1 ? "text-dark" : "text-muted"}`}>Address</span>
              <span className={`small fw-semibold ${step >= 2 ? "text-dark" : "text-muted"}`}>Review</span>
              <span className={`small fw-semibold ${step >= 3 ? "text-dark" : "text-muted"}`}>Verify</span>
              <span className={`small fw-semibold ${step >= 4 ? "text-dark" : "text-muted"}`}>Payment</span>
            </div>
          </div>
        </div>
      </div>

      {/* STEP 1: ADDRESS SELECTION */}
      {step === 1 && (
        <div className="animate-slide-in">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card border-0 shadow-sm rounded-4 p-4 checkout-card">
                <AddressManager
                  onAddressSelect={handleAddressSelect}
                  selectedAddressId={selectedAddressId}
                />

                {selectedAddress && (
                  <div className="mt-4 pt-4 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="fw-bold mb-1">Selected Address:</h6>
                        <p className="text-muted mb-0">
                          {selectedAddress.fullName} - {selectedAddress.city},{" "}
                          {selectedAddress.state}
                        </p>
                      </div>
                      <button
                        className="btn btn-dark rounded-pill px-4 py-3"
                        style={{ background: "#2f0f0c" }}
                        onClick={handleContinueToReview}
                      >
                        Continue to Review
                        <i className="bi bi-arrow-right ms-2"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: REVIEW */}
      {step === 2 && (
        <div className="animate-slide-in">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-4 p-4 checkout-card mb-4">
                <h5 className="fw-bold mb-4">
                  <i className="bi bi-clipboard-check me-2 text-primary"></i>
                  Order Review
                </h5>

                {/* DELIVERY ADDRESS */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Delivery Address</h6>
                  <div className="card bg-light border-0 p-4 rounded-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 me-2">
                            {selectedAddress?.label === "Home"
                              ? "🏠"
                              : selectedAddress?.label === "Office"
                                ? "🏢"
                                : "📍"}{" "}
                            {selectedAddress?.label}
                          </span>
                        </div>
                        <p className="mb-1 fw-semibold">
                          {selectedAddress?.fullName}
                        </p>
                        <p className="mb-1 text-muted">
                          {selectedAddress?.addressLine}
                        </p>
                        <p className="mb-1 text-muted">
                          {selectedAddress?.city}, {selectedAddress?.state} -{" "}
                          {selectedAddress?.pincode}
                        </p>
                        <p className="text-muted small mb-0">
                          <i className="bi bi-telephone me-1"></i>
                          {selectedAddress?.phone}
                        </p>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary rounded-pill"
                        onClick={() => setStep(1)}
                      >
                        <i className="bi bi-pencil me-1"></i>Change
                      </button>
                    </div>
                  </div>
                </div>

                {/* ORDER ITEMS */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Order Items ({items.length})</h6>
                  <div className="card border-0 bg-light rounded-3">
                    {items.map((item, index) => (
                      <div
                        key={item.id}
                        className={`p-3 ${index < items.length - 1 ? "border-bottom" : ""}`}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div
                              className="bg-white rounded-3 p-2 me-3"
                              style={{ width: "50px", height: "50px" }}
                            >
                              <i
                                className="bi bi-box text-muted"
                                style={{ fontSize: "24px" }}
                              ></i>
                            </div>
                            <div>
                              <h6 className="mb-1 fw-semibold">{item.name}</h6>
                              <small className="text-muted">
                                Quantity: {item.qty || 1}
                              </small>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="fw-bold">
                              ₹{(item.price * (item.qty || 1)).toLocaleString()}
                            </div>
                            <small className="text-muted">
                              ₹{item.price} each
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ORDER SUMMARY */}
                <div className="card bg-primary-subtle border-0 rounded-3 p-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal ({items.length} items)</span>
                    <span>₹{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>GST (18%)</span>
                    <span>₹{gst.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Delivery Charges</span>
                    <span className="text-success fw-semibold">FREE</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Packaging Charges</span>
                    <span className="text-success fw-semibold">FREE</span>
                  </div>
                  <hr className="my-3" />
                  <div className="d-flex justify-content-between fw-bold fs-5">
                    <span>Total Amount</span>
                    <span className="text-success">
                      ₹{total.toLocaleString()}
                    </span>
                  </div>
                  <small className="text-muted mt-2">GST (18%) included</small>
                </div>
              </div>

              <div className="d-flex gap-3">
                <button
                  className="btn btn-outline-dark flex-grow-1 rounded-pill py-3"
                  onClick={() => setStep(1)}
                >
                  <i className="bi bi-arrow-left me-2"></i>Back to Address
                </button>
                <button
                  className="btn btn-dark flex-grow-1 rounded-pill py-3"
                  style={{ background: "#2f0f0c" }}
                  onClick={handlePayment}
                >
                  Continue to Verify
                  <i className="bi bi-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: PHONE OTP VERIFICATION */}
      {step === 3 && (
        <div className="animate-slide-in">
          <div className="row justify-content-center">
            <div className="col-lg-5">
              <div className="card border-0 shadow-sm rounded-4 p-5 checkout-card">
                <div className="text-center mb-4">
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 80, height: 80, background: "#e7f9ee" }}>
                    <i className="bi bi-whatsapp" style={{ fontSize: 36, color: "#25D366" }}></i>
                  </div>
                  <h2 className="fw-bold mb-2">Verify via WhatsApp</h2>
                  <p className="text-muted">We'll send a 6-digit OTP to your WhatsApp before payment</p>
                </div>

                {!otpVerified ? (
                  <>
                    {/* Phone input */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">WhatsApp Number</label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0">+91</span>
                        <input
                          type="tel"
                          className="form-control border-start-0"
                          placeholder="10-digit WhatsApp number"
                          maxLength={10}
                          value={phone}
                          onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                          disabled={otpSent}
                        />
                        {otpSent && (
                          <button className="btn btn-outline-secondary" onClick={() => { setOtpSent(false); setOtp(""); setCountdown(0); }}>
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {!otpSent ? (
                      <button
                        className="btn w-100 rounded-pill py-3 fw-bold text-white mb-3"
                        style={{ background: "linear-gradient(135deg,#2f0f0c,#4a1a15)" }}
                        onClick={handleSendOtp}
                        disabled={otpLoading || phone.length < 10}
                      >
                        {otpLoading
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                          : <><i className="bi bi-whatsapp me-2"></i>Send OTP on WhatsApp</>}
                      </button>
                    ) : (
                      <>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Enter OTP</label>
                          <input
                            type="text"
                            className="form-control form-control-lg text-center fw-bold"
                            placeholder="------"
                            maxLength={6}
                            value={otp}
                            onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                            style={{ letterSpacing: 8, fontSize: "1.4rem" }}
                          />
                          <div className="d-flex justify-content-between mt-2">
                            <small className="text-muted">
                              {countdown > 0
                                ? `Expires in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`
                                : "OTP expired"}
                            </small>
                            <button
                              className="btn btn-link btn-sm p-0 text-decoration-none"
                              style={{ color: "#2f0f0c" }}
                              onClick={() => { setOtpSent(false); setOtp(""); }}
                              disabled={countdown > 240}
                            >
                              Resend OTP
                            </button>
                          </div>
                        </div>
                        <button
                          className="btn w-100 rounded-pill py-3 fw-bold text-white mb-3"
                          style={{ background: "linear-gradient(135deg,#2f0f0c,#4a1a15)" }}
                          onClick={handleVerifyOtp}
                          disabled={otpLoading || otp.length < 4}
                        >
                          {otpLoading
                            ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                            : <><i className="bi bi-shield-check me-2"></i>Verify OTP</>}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center py-3">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: 64, height: 64, background: "#d4edda" }}>
                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 32 }}></i>
                    </div>
                    <h5 className="fw-bold mb-1" style={{ color: "#25D366" }}>WhatsApp Verified!</h5>
                    <p className="text-muted mb-4">+91 {phone}</p>
                    <button
                      className="btn w-100 rounded-pill py-3 fw-bold text-white"
                      style={{ background: "linear-gradient(135deg,#2f0f0c,#4a1a15)" }}
                      onClick={() => setStep(4)}
                    >
                      <i className="bi bi-credit-card me-2"></i>Proceed to Payment
                    </button>
                  </div>
                )}

                <button className="btn btn-outline-secondary w-100 rounded-pill py-2 mt-2" onClick={() => setStep(2)}>
                  <i className="bi bi-arrow-left me-2"></i>Back to Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: PAYMENT */}
      {step === 4 && (
        <div className="animate-slide-in">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm rounded-4 p-5 checkout-card">
                <div className="text-center mb-4">
                  <div
                    className="bg-success-subtle rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <i
                      className="bi bi-credit-card text-success"
                      style={{ fontSize: "32px" }}
                    ></i>
                  </div>
                  <h2 className="fw-bold mb-2">Secure Payment</h2>
                  <p className="text-muted">
                    Complete your order with secure payment
                  </p>
                </div>

                {/* PAYMENT SUMMARY */}
                <div className="card bg-light border-0 rounded-3 p-4 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-semibold">Order Summary</span>
                    <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">
                      {items.length} item{items.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>GST (18%)</span>
                    <span>₹{gst.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Delivery</span>
                    <span className="text-success fw-semibold">FREE</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Taxes & Fees</span>
                    <span className="text-success fw-semibold">Included</span>
                  </div>
                  <hr className="my-3" />
                  <div className="d-flex justify-content-between fw-bold fs-4">
                    <span>Total</span>
                    <span className="text-success">
                      ₹{total.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* PAYMENT METHOD */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Payment Method</h6>
                  <div className="card border-2 border-primary bg-primary-subtle rounded-3 p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="bg-white rounded-3 p-2 me-3">
                          <img
                            src="https://razorpay.com/assets/razorpay-logo.svg"
                            alt="Razorpay"
                            style={{ height: "24px" }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold">
                            Razorpay Secure Payment
                          </div>
                          <small className="text-muted">
                            Cards, UPI, Net Banking, Wallets
                          </small>
                        </div>
                      </div>
                      <i
                        className="bi bi-shield-check text-success"
                        style={{ fontSize: "24px" }}
                      ></i>
                    </div>
                  </div>
                </div>

                {/* SECURITY INFO */}
                <div className="alert alert-info border-0 rounded-3 mb-4">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-shield-lock me-2"></i>
                    <small>
                      Your payment information is encrypted and secure. We don't
                      store your card details.
                    </small>
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-dark btn-lg rounded-pill py-3 fw-bold"
                    style={{ background: "linear-gradient(135deg, #2f0f0c, #4a1a15)" }}
                    onClick={handleRazorpayPayment}
                    disabled={paying}
                  >
                    {paying ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                    ) : (
                      <><i className="bi bi-lock me-2"></i>Pay ₹{total.toLocaleString()} Securely</>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary rounded-pill py-3"
                    onClick={() => setStep(3)}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Verify
                  </button>
                </div>

                {/* TRUST INDICATORS */}
                <div className="text-center mt-4">
                  <div className="d-flex justify-content-center align-items-center gap-3 text-muted small">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-shield-check me-1"></i>
                      SSL Secured
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-credit-card me-1"></i>
                      PCI Compliant
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-bank me-1"></i>
                      Bank Grade Security
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
